<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, onMounted, ref } from 'vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import {
    CATALOG_FILTERS,
    TCatalogFilter,
  } from '@/domain/catalog/catalog-filters';
  import { catalogFilterKey } from '@/domain/catalog/catalog-filter-copy';

  /**
   * The «menu health» chips (plan §5.7): «Todos» always, the rest while they
   * have products or are active. On phones' find mode the first chip is the
   * result count, which clears the search.
   */
  const props = withDefaults(
    defineProps<{
      /** The white phone head: idle chips are grey instead of outlined. */
      phone?: boolean;
      /** Find mode: «7 resultados ✕» first. */
      resultChip?: boolean;
    }>(),
    { phone: false, resultChip: false }
  );

  const { state, data, url } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const scroller = ref<HTMLElement | null>(null);

  const chips = computed(() => {
    // Read once: every read of the counts walks all the products' dependencies.
    const counts = data.counts.value;
    return CATALOG_FILTERS.filter(
      (filter) =>
        filter === 'all' || filter === state.filter || counts[filter] > 0
    ).map((filter) => ({
      filter,
      label: String(proxy.$t(`menuPage.filter.${catalogFilterKey(filter)}`)),
      count: filter === 'all' ? null : counts[filter],
    }));
  });

  const resultText = computed(() =>
    String(
      proxy.$tc('menuPage.result.count', data.shownCount.value, {
        n: data.shownCount.value,
      })
    )
  );

  function choose(filter: TCatalogFilter) {
    if (filter !== state.filter) url.setFilter(filter);
  }

  /** The active chip in view, by the scroller's own offset (never the page's). */
  async function revealActive() {
    await nextTick();
    const box = scroller.value;
    const active = box?.querySelector<HTMLElement>('.filter-chip.is-active');
    if (!box || !active) return;
    const right = active.offsetLeft + active.offsetWidth;
    if (
      active.offsetLeft < box.scrollLeft ||
      right > box.scrollLeft + box.clientWidth
    )
      box.scrollLeft = Math.max(0, active.offsetLeft - 16);
  }

  // A new row starts scrolled to its start, where «Todos» already is: only
  // another filter needs revealing (measuring would force a layout).
  onMounted(() => {
    if (state.filter !== 'all') void revealActive();
  });
</script>

<template>
  <div
    ref="scroller"
    class="v2-catalog-filter-chips"
    :class="{ 'is-phone': phone }"
    role="group"
    :aria-label="$t('menuPage.filter.label')"
  >
    <button
      v-if="resultChip"
      type="button"
      class="filter-chip is-result"
      :aria-label="`${resultText}. ${$t('menuPage.result.clearSearch')}`"
      @click="url.clearSearch()"
    >
      {{ resultText }}<i class="mdi mdi-close chip-clear" />
    </button>
    <button
      v-for="chip in chips"
      :key="chip.filter"
      type="button"
      class="filter-chip"
      :class="{ 'is-active': chip.filter === state.filter }"
      :aria-pressed="chip.filter === state.filter ? 'true' : 'false'"
      :aria-label="
        chip.count !== null ? `${chip.label} (${chip.count})` : chip.label
      "
      @click="choose(chip.filter)"
    >
      {{ chip.label
      }}<span v-if="chip.count !== null" class="chip-count">{{
        chip.count
      }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-filter-chips {
    // Relative: the chips' offsetLeft is measured from the scroller.
    @apply tw-relative tw-flex tw-min-w-0 tw-flex-nowrap tw-items-center tw-overflow-x-auto tw-py-1;
    scrollbar-width: none;
    // The right edge fades: more chips scroll in.
    -webkit-mask-image: linear-gradient(
      to right,
      black calc(100% - 24px),
      transparent
    );
    mask-image: linear-gradient(to right, black calc(100% - 24px), transparent);

    &::-webkit-scrollbar {
      display: none;
    }

    .filter-chip {
      @apply tw-relative tw-mr-2 tw-inline-flex tw-h-9 tw-flex-none tw-cursor-pointer tw-items-center tw-whitespace-nowrap tw-rounded-full tw-border-0 tw-bg-surface tw-px-3.5 tw-text-sm tw-font-medium tw-text-neutral-700 tw-transition-colors tw-duration-150;
      box-shadow: inset 0 0 0 1px rgb(var(--c-neutral-200));

      // 36 px visual, 44 px to the touch.
      &::after {
        @apply tw-absolute tw-left-0 tw-right-0;
        content: '';
        top: -4px;
        bottom: -4px;
      }

      &:last-child {
        @apply tw-mr-6;
      }

      &:hover {
        @apply tw-bg-neutral-50;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      .chip-count {
        @apply tw-ml-1.5 tw-tabular-nums tw-text-neutral-500;
      }

      .chip-clear {
        @apply tw-ml-1.5 tw-text-base tw-leading-none;
      }

      &.is-active,
      &.is-result {
        @apply tw-bg-neutral-900 tw-text-on-inverse;
        box-shadow: none;

        .chip-count {
          @apply tw-text-on-inverse;
          opacity: 0.7;
        }
      }
    }

    // The phone head is white: idle chips are grey fills.
    &.is-phone .filter-chip:not(.is-active):not(.is-result) {
      @apply tw-bg-neutral-100;
      box-shadow: none;
    }
  }
</style>
